import React, { useEffect, useRef } from 'react';
import { cameraAtT, cameraFor, computeDip, durationFor, type Box, type Camera, type Stage } from './cameraMath';
import './canvasStage.css';

interface CanvasStageProps {
  board: { width: number; height: number };
  targetBox: Box | null;
  reducedMotion: boolean;
  /** Camera bevriest zolang blackout true is -- spiegelt het prototype's
   * refresh() ("if (!blackout) animateTo(...)"): navigatie zelf blijft
   * gewoon werken (targetBox mag wijzigen), alleen de zichtbare camerabeweging
   * wordt overgeslagen tot blackout weer uitgaat. */
  blackout: boolean;
  children: React.ReactNode;
}

/**
 * Camera/animatie-host: poort van het prototype's stageSize()/applyCamera()/
 * snapTo()/animateTo() op refs i.p.v. module-level closures. useRef moet
 * render-cycli overleven zonder zelf een re-render te triggeren -- de juiste
 * keuze voor 60fps style.transform-writes buiten de VDOM om.
 */
export default function CanvasStage({ board, targetBox, reducedMotion, blackout, children }: CanvasStageProps) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const currentCameraRef = useRef<Camera | null>(null);
  const animHandleRef = useRef<number | null>(null);

  useEffect(() => {
    function stageSize(): Stage {
      const rect = stageRef.current?.getBoundingClientRect();
      return { w: rect?.width || 0, h: rect?.height || 0 };
    }

    function applyCamera(camera: Camera) {
      if (boardRef.current) {
        boardRef.current.style.transform = `translate(${camera.tx}px, ${camera.ty}px) scale(${camera.scale})`;
      }
    }

    function animateTo(box: Box) {
      const stage = stageSize();
      const dest = cameraFor(box, stage);
      if (animHandleRef.current !== null) {
        cancelAnimationFrame(animHandleRef.current);
        animHandleRef.current = null;
      }
      // Geen bestaande camerastand (eerste render) of reduced motion: direct
      // toepassen, geen rAF-lus -- MVP-acceptatiecriterium 27.13.
      if (!currentCameraRef.current || reducedMotion) {
        currentCameraRef.current = dest;
        applyCamera(dest);
        return;
      }

      const src = currentCameraRef.current;
      const dip = computeDip(src, dest, stage);
      const duration = durationFor(src, dest, stage);
      let start: number | null = null;

      const step = (timestamp: number) => {
        if (start === null) start = timestamp;
        const rawT = Math.min(1, (timestamp - start) / duration);
        applyCamera(cameraAtT(src, dest, dip, rawT, stage));
        if (rawT < 1) {
          animHandleRef.current = requestAnimationFrame(step);
        } else {
          currentCameraRef.current = dest;
          animHandleRef.current = null;
        }
      };
      animHandleRef.current = requestAnimationFrame(step);
    }

    if (targetBox && !blackout) animateTo(targetBox);

    return () => {
      if (animHandleRef.current !== null) {
        cancelAnimationFrame(animHandleRef.current);
        animHandleRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetBox, reducedMotion, blackout]);

  return (
    <div ref={stageRef} className="canvas-stage">
      <div ref={boardRef} className="canvas-board" style={{ width: board.width, height: board.height }}>
        {children}
      </div>
    </div>
  );
}
